import { useState, useRef, useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";
import ClientSignatureField from "../components/ClientSignatureField";
import { signatureProblem, signaturePayload } from "../lib/clientSignature";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Users, Loader2, AlertCircle } from "lucide-react";
// Firm identity from the tenant fixture (PRD_PHASE_1A_FIRM.md §3.2.1).
import { FIRM } from "../fixtures/firmIdentity";
import axios from "axios";
import { TOB_SECTIONS, TOB_VERSION } from "./onboarding/tobContent";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";
import { LOGO_URL, KOI_BG_URL } from "../config/constants";

// BM-H3490: residential matter types. At OB time the form only knows the
// practice area ("01 RESI", from the conflict check); the specific codes are
// listed for links made after the FORF. Vanessa, 3 Oct 2026: "only resi".
const RESI_MATTER_TYPES = ["01 RESI", "purchase_resi", "sale_resi", "remortgage", "remortgage_toe", "toe_only", "auction_review", "declaration_trust", "restrictive_covenant", "licence_to_assign", "licence_alterations", "title_split"];

// BM-H3668: SDLT is also filed on commercial property purchases and leases
// (Sangita, via Vanessa, 5 Oct 2026), so the SDLT tax reference questions
// (NI number, CoHo auth code) show there too. "03 Commercial Property" is
// the practice area the form gets from the conflict check at OB time.
const COMMERCIAL_PROPERTY_MATTER_TYPES = ["03 Commercial Property", "comm_freehold_purchase", "comm_freehold_sale", "comm_lease_landlord", "comm_lease_tenant", "comm_lease_assignment", "comm_lease_surrender", "comm_lease_assgn_surr"];
const SDLT_MATTER_TYPES = [...RESI_MATTER_TYPES, ...COMMERCIAL_PROPERTY_MATTER_TYPES];

const koiStyle = {
  position: "fixed",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  objectPosition: "center",
  opacity: 0.18,
  zIndex: 0,
  pointerEvents: "none",
  userSelect: "none",
};

export default function JointClientOnboarding() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [fetchError, setFetchError] = useState("");

  const [step, setStep] = useState("tob");
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [tobAccepted, setTobAccepted] = useState(false);
  const [formData, setFormData] = useState({
    full_name: "", email: "", phone: "", mobile: "", address: "", capacity: "",
    // BM-H421: the same client due diligence the primary is asked for. A
    // joint client is a client and the firm owes them the same file.
    date_of_birth: "", nationality: "", occupation: "",
    national_insurance_number: "", acting_for_self: "", acting_for_self_details: "",
    // Stage B4: joint clients now capture their own entity_type instead of
    // inheriting the primary's. Default seeded from the OB invite metadata
    // so the common case (joint individuals on a property purchase) doesn't
    // require a click — but the FE shows the field so the user can change it.
    entity_type: "",
  });
  const [estateAgentAuthority, setEstateAgentAuthority] = useState("");
  const [estateAgentIdDisclosure, setEstateAgentIdDisclosure] = useState("");
  const [estateAgentDetailLimit, setEstateAgentDetailLimit] = useState("");
  const [estateAgentNames, setEstateAgentNames] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState([]);
  const [success, setSuccess] = useState(false);
  // BM-H3498: was purchase/sale only, which the form never learned, so it never showed.
  const showEstateAgentSection = RESI_MATTER_TYPES.includes(info?.matter_type);
  const jointIsIndividual = formData.entity_type === "individual";
  const showJointNiNumber =
    SDLT_MATTER_TYPES.includes(info?.matter_type) && jointIsIndividual;
  const sigCanvasRef = useRef(null);
  // BM-H588: sign by drawing or by typing the full name.
  const [sigMode, setSigMode] = useState("draw");
  const [typedSignature, setTypedSignature] = useState("");
  const tobContainerRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/onboarding/joint/${token}`);
        setInfo(res.data);
        setFormData(prev => ({
          ...prev,
          full_name: res.data.joint_client_name || "",
          email: res.data.joint_client_email || "",
        }));
      } catch (err) {
        setFetchError(errorMessage(err, "Invalid or expired onboarding link."));
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleScroll = useCallback(() => {
    const el = tobContainerRef.current;
    if (!el) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 40) setHasScrolledToBottom(true);
  }, []);

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const missing = [];

    if (!formData.full_name) missing.push("Full name");
    if (!formData.email) missing.push("Email address");
    if (!formData.phone) missing.push("Phone number");
    if (!formData.address) missing.push("Address");
    // Stage B4: joint clients now declare their own entity_type.
    if (!formData.entity_type) missing.push("Acting as (entity type)");
    // BM-H421: parity with the primary onboarding form.
    if (jointIsIndividual) {
      if (!formData.date_of_birth) missing.push("Date of birth");
      if (!formData.nationality) missing.push("Nationality");
      if (!formData.occupation) missing.push("Occupation");
      if (!formData.acting_for_self) missing.push("Whether you are acting on your own behalf");
      if (formData.acting_for_self === "no" && !formData.acting_for_self_details) {
        missing.push("Details of who you are acting for");
      }
    }
    if (!tobAccepted) missing.push("Agreement to Terms of Business");
    {
      const sigIssue = signatureProblem({
        mode: sigMode,
        typedName: typedSignature,
        fullName: formData.full_name,
        canvasEmpty: sigMode === "draw" ? (sigCanvasRef.current ? sigCanvasRef.current.isEmpty() : true) : false,
      });
      if (sigIssue) missing.push(sigIssue);
    }

    if (missing.length > 0) {
      setErrors(missing);
      return;
    }

    setErrors([]);
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        signature: signaturePayload({ mode: sigMode, typedName: typedSignature, canvasRef: sigCanvasRef }),
        agreed_tob: true,
        tob_version: TOB_VERSION, // BM-H3379
      };
      if (showEstateAgentSection) {
        payload.estate_agent_authority = estateAgentAuthority || null;
        payload.estate_agent_id_disclosure = estateAgentAuthority === "yes" ? (estateAgentIdDisclosure || null) : null;
        payload.estate_agent_detail_limit = estateAgentDetailLimit || null;
        payload.estate_agent_names = estateAgentAuthority === "yes" ? (estateAgentNames || null) : null;
      }
      await axios.post(`${API}/onboarding/joint/${token}/submit`, payload);
      setSuccess(true);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;

      // 4xx — actionable validation errors. Surface message verbatim.
      // Crucially: never setErrors([detail]) when detail is an object —
      // that renders [object Object] in the React tree.
      if (status && status >= 400 && status < 500) {
        let message = "Submission failed. Please try again.";
        if (typeof detail === "string") {
          message = detail;
        } else if (Array.isArray(detail) && detail.length > 0) {
          const fields = detail.map(d => {
            const field = Array.isArray(d.loc) ? d.loc.slice(1).join('.') : 'field';
            return `${field}: ${d.msg}`;
          }).join('; ');
          message = fields || 'Validation failed';
        } else if (detail && typeof detail === "object" && detail.message) {
          message = detail.message;
        }
        setErrors([message]);
        return;
      }

      // 5xx — server-side, never surface raw detail; show a generic apology.
      // See client_portal_ob_error_incident_28May2026.md.
      console.error("Joint OB submit server error", { status, detail, err });
      const generic = (detail && typeof detail === "object" && typeof detail.message === "string")
        ? detail.message
        : "Something went wrong on our side. Your details have been recorded and our team has been notified. Please contact us at clientcare@bonsai.law if you do not hear from us within one business day.";
      setErrors([generic]);
    } finally {
      setSubmitting(false);
    }
  };

  const Shell = ({ children, centered = false }) => (
    <div className="relative min-h-screen bg-navy">
      <img src={KOI_BG_URL} alt="" aria-hidden="true" style={koiStyle} />
      <div className={`relative z-10 ${centered ? "flex items-center justify-center min-h-screen p-4" : ""}`}>
        {children}
      </div>
    </div>
  );

  if (loading) return (
    <Shell centered>
      <Loader2 className="w-8 h-8 animate-spin text-teal" />
    </Shell>
  );

  if (fetchError) return (
    <Shell centered>
      <div className="max-w-md w-full text-center">
        <img src={LOGO_URL} alt="Bonsai Law" className="h-16 mx-auto mb-8" />
        <div className="glass-card p-8">
          <p className="text-status-red font-medium mb-2">{fetchError}</p>
          <p className="text-slate-500 text-sm">Please contact <a href="mailto:clientcare@bonsai.law" className="text-teal hover:underline" target="_blank" rel="noopener noreferrer">clientcare@bonsai.law</a> for assistance.</p>
        </div>
      </div>
    </Shell>
  );

  if (success) return (
    <Shell centered>
      <div className="max-w-lg w-full text-center" data-testid="joint-onboarding-success">
        <img src={LOGO_URL} alt="Bonsai Law" className="h-16 mx-auto mb-8" />
        <div className="glass-card p-8">
          <div className="w-16 h-16 bg-teal-dim/40 rounded-md flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-teal" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Onboarding Complete</h2>
          <p className="text-slate-400 text-sm">
            Thank you, {formData.full_name}. Your acceptance of the Terms of Business has been recorded.
          </p>
          {info?.provisional_id && (
            <p className="text-xs text-slate-500 mt-3">Reference: <span className="font-mono font-medium text-slate-300">{info.provisional_id}</span></p>
          )}
        </div>
      </div>
    </Shell>
  );

  return (
    <Shell>
      <div className="glass-nav py-4 px-6">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <img src={LOGO_URL} alt="Bonsai Law" className="h-10" />
          <span className="text-xs text-slate-400">Joint Client Onboarding</span>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        <div className="mb-4 p-3 bg-blue-900/20 rounded-md border border-blue-800/30 flex items-start gap-2" data-testid="joint-context-banner">
          <Users className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />
          <div className="text-sm text-blue-300">
            <p className="font-medium">Joint client onboarding for reference {info?.provisional_id}</p>
            <p className="text-xs text-blue-400 mt-0.5">
              Primary client: {info?.primary_client_name}
              {info?.company_name ? ` (${info.company_name})` : ""}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 mb-6">
          <StepBadge num="1" label="Terms of Business" active={step === "tob"} done={step === "form"} />
          <div className="h-px flex-1 bg-navy-hover" />
          <StepBadge num="2" label="Your Details" active={step === "form"} done={false} />
        </div>

        {step === "tob" && (
          <div data-testid="joint-tob-step">
            <div className="glass-card overflow-hidden">
              <div className="p-5 border-b border-navy-border">
                <h1 className="text-xl font-bold text-white">Bonsai Law Ltd — Terms of Business</h1>
                <p className="text-sm text-slate-500 mt-1">{TOB_VERSION}. Please read all terms carefully</p>
              </div>
              <div ref={tobContainerRef} onScroll={handleScroll}
                className="h-[50vh] overflow-y-auto p-5 space-y-6 text-sm text-txt leading-relaxed"
                data-testid="joint-tob-scroll">
                {TOB_SECTIONS.map((s, i) => (
                  <div key={i}>
                    <h3 className="font-semibold text-white mb-2">{s.title}</h3>
                    <p className="whitespace-pre-line">{s.content}</p>
                  </div>
                ))}
                <div className="pt-4 border-t border-navy-border">
                  <p className="text-xs text-slate-400 text-center">{FIRM.name} | Terms of Business | {TOB_VERSION}</p>
                </div>
              </div>
              <div className="p-5 border-t border-navy-border bg-navy-hover/60 space-y-3">
                {!hasScrolledToBottom && (
                  <p className="text-xs text-status-amber text-center">Please scroll to the bottom to continue</p>
                )}
                <div className="flex items-start gap-2">
                  <Checkbox id="jt-tob" checked={tobAccepted} onCheckedChange={setTobAccepted} disabled={!hasScrolledToBottom} data-testid="joint-tob-checkbox" />
                  <label htmlFor="jt-tob" className={`text-sm leading-snug ${hasScrolledToBottom ? "text-txt" : "text-slate-500"}`}>
                    I confirm that I have read and agree to {FIRM.name}'s Terms of Business ({TOB_VERSION}).
                  </label>
                </div>
                <Button onClick={() => setStep("form")} disabled={!hasScrolledToBottom || !tobAccepted}
                  className="w-full h-11 bg-teal hover:bg-teal-hover text-white disabled:opacity-40"
                  data-testid="joint-proceed-button">
                  Continue to Your Details
                </Button>
              </div>
            </div>
          </div>
        )}

        {step === "form" && (
          <div data-testid="joint-form-step">
            <div className="glass-card overflow-hidden">
              <div className="p-5 border-b border-navy-border">
                <h1 className="text-xl font-bold text-white">Your Details</h1>
                <p className="text-sm text-slate-500 mt-1">Please complete your information below.</p>
              </div>
              <form onSubmit={handleSubmit} className="p-5 space-y-5">
                <div className="space-y-4">
                  {/* BM-H604: a client form never searches the firm's contacts. The old
                      staff contact search here called a staff-only route, which sent the
                      client to the staff M365 login, and could offer other clients' details. */}
                  <div className="space-y-1.5">
                    <Label className="text-txt text-sm">Full Name <span className="text-status-red">*</span></Label>
                    <Input type="text" name="full_name" autoComplete="name" value={formData.full_name} onChange={e => handleChange("full_name", e.target.value)} placeholder="Full name" className="h-10" data-testid="full-name-input" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-txt text-sm">Email <span className="text-status-red">*</span></Label>
                      <Input type="email" value={formData.email} onChange={e => handleChange("email", e.target.value)} className="h-10" data-testid="joint-email-input" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-txt text-sm">Phone <span className="text-status-red">*</span></Label>
                      <Input type="tel" value={formData.phone} onChange={e => handleChange("phone", e.target.value)} className="h-10" data-testid="joint-phone-input" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-txt text-sm">Mobile Number</Label>
                    <Input type="tel" value={formData.mobile} onChange={e => handleChange("mobile", e.target.value)} placeholder="Mobile number (for ID verification)" className="h-10" data-testid="joint-mobile-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-txt text-sm">Address <span className="text-status-red">*</span></Label>
                    <Textarea value={formData.address} onChange={e => handleChange("address", e.target.value)} rows={3} className="resize-none" data-testid="joint-address-input" />
                  </div>
                  {/* Stage B4: capture joint client's own entity_type. */}
                  <div className="space-y-1.5">
                    <Label className="text-txt text-sm">I am acting as <span className="text-status-red">*</span></Label>
                    <select
                      value={formData.entity_type}
                      onChange={(e) => handleChange("entity_type", e.target.value)}
                      className="w-full h-10 px-3 rounded-md border border-navy-border bg-white/5 text-white text-sm"
                      data-testid="joint-entity-type-select"
                      required
                    >
                      <option value="">Select...</option>
                      <option value="individual">Individual</option>
                      <option value="company">Company</option>
                      <option value="llp">LLP</option>
                      <option value="other">Other</option>
                    </select>
                    <p className="text-[11px] text-slate-500">
                      Used for your AML profile and the matter's client file.
                    </p>
                  </div>
                  {(["company", "llp"].includes(formData.entity_type) ||
                    ["company", "llp"].includes(info?.entity_type)) && (
                    <div className="space-y-1.5">
                      <Label className="text-txt text-sm">Your capacity (e.g. director, member, authorised signatory)</Label>
                      <select value={formData.capacity} onChange={e => handleChange("capacity", e.target.value)}
                        className="w-full h-10 px-3 rounded-md border border-navy-border bg-white/5 text-white text-sm" data-testid="joint-capacity-select">
                        <option value="">Select...</option>
                        <option value="Director">Director</option>
                        <option value="Member">Member</option>
                        <option value="Authorised Signatory">Authorised Signatory</option>
                        <option value="Secretary">Secretary</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  )}

                  {/* BM-H421: same client due diligence as the primary. */}
                  {jointIsIndividual && (
                    <div className="space-y-4 p-4 bg-navy-hover/60 rounded-md border border-navy-border" data-testid="joint-individual-fields">
                      <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">Your Client Details</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <Label className="text-txt text-sm">Date of Birth <span className="text-status-red">*</span></Label>
                          <Input type="date" max={new Date().toISOString().slice(0, 10)} min="1900-01-01" value={formData.date_of_birth} onChange={e => handleChange("date_of_birth", e.target.value)} className="h-10" data-testid="joint-dob-input" />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-txt text-sm">Nationality <span className="text-status-red">*</span></Label>
                          <Input value={formData.nationality} onChange={e => handleChange("nationality", e.target.value)} placeholder="e.g. British" className="h-10" data-testid="joint-nationality-input" />
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-txt text-sm">Occupation <span className="text-status-red">*</span></Label>
                        <Input value={formData.occupation} onChange={e => handleChange("occupation", e.target.value)} placeholder="e.g. Software Engineer" className="h-10" data-testid="joint-occupation-input" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-txt text-sm">Are you acting in your own name and on your own behalf? <span className="text-status-red">*</span></Label>
                        <RadioGroup value={formData.acting_for_self} onValueChange={v => handleChange("acting_for_self", v)} data-testid="joint-acting-for-self">
                          <div className="flex items-center gap-2">
                            <RadioGroupItem value="yes" id="jt-afs-yes" />
                            <label htmlFor="jt-afs-yes" className="text-sm text-txt-muted">Yes, I am the sole beneficial owner</label>
                          </div>
                          <div className="flex items-center gap-2">
                            <RadioGroupItem value="no" id="jt-afs-no" />
                            <label htmlFor="jt-afs-no" className="text-sm text-txt-muted">No, I am acting on behalf of another person or entity</label>
                          </div>
                        </RadioGroup>
                        {formData.acting_for_self === "no" && (
                          <div className="space-y-1.5 mt-2 p-3 bg-navy-hover/60 rounded-md border border-navy-border" data-testid="joint-acting-for-self-details-section">
                            <Label className="text-txt text-sm">Please give brief details <span className="text-status-red">*</span></Label>
                            <p className="text-xs text-slate-400">Whose behalf are you acting on, your relationship to them, and any written authority you hold (e.g. Power of Attorney, executor of estate, parent of minor, company directorship).</p>
                            <textarea
                              value={formData.acting_for_self_details}
                              onChange={e => handleChange("acting_for_self_details", e.target.value)}
                              placeholder="e.g. Acting for my mother under a registered LPA dated 12 March 2024."
                              className="w-full min-h-[80px] p-2 rounded-md border border-navy-border bg-navy-card text-txt text-sm"
                              data-testid="joint-acting-for-self-details-input"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {showJointNiNumber && (
                    <div className="space-y-2 p-4 bg-navy-hover/60 rounded-md border border-navy-border" data-testid="joint-ni-number-section">
                      <div className="space-y-1.5">
                        <Label className="text-txt text-sm">National Insurance Number</Label>
                        <p className="text-xs text-slate-400">
                          If you are buying a property or taking a lease, we need your National Insurance number to complete the Stamp Duty Land Tax (SDLT) return we submit to HMRC on your behalf. If you are not, you can leave this blank.
                        </p>
                        <Input value={formData.national_insurance_number} onChange={e => handleChange("national_insurance_number", e.target.value)} placeholder="e.g. AB 12 34 56 C" className="h-10" data-testid="joint-ni-number-input" />
                      </div>
                    </div>
                  )}
                </div>

                {showEstateAgentSection && (
                  <div className="space-y-4 p-4 bg-navy-hover/60 rounded-md border border-navy-border" data-testid="joint-estate-agent-authority-section">
                    <div>
                      <Label className="text-txt font-medium">Authority to speak to the estate agent</Label>
                      <p className="text-xs text-slate-500 mt-0.5">If you are buying or selling through an estate agent, please tell us how we may communicate with them. If no estate agent is involved, you can skip these questions.</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-txt text-sm">Do you authorise us to discuss the progress of your transaction with the estate agent(s) involved?</Label>
                      <RadioGroup value={estateAgentAuthority} onValueChange={setEstateAgentAuthority} data-testid="joint-ea-authority">
                        <div className="flex items-center gap-2"><RadioGroupItem value="yes" id="jea-auth-yes" /><label htmlFor="jea-auth-yes" className="text-sm text-txt-muted">Yes, I authorise this</label></div>
                        <div className="flex items-center gap-2"><RadioGroupItem value="no" id="jea-auth-no" /><label htmlFor="jea-auth-no" className="text-sm text-txt-muted">No, I do not authorise this</label></div>
                      </RadioGroup>
                    </div>
                    {estateAgentAuthority === "yes" && (
                      <div className="space-y-2">
                        <Label className="text-txt text-sm">Do you also authorise us to tell the estate agent(s) that we are waiting for standard client due diligence documents, without sharing detailed content?</Label>
                        <RadioGroup value={estateAgentIdDisclosure} onValueChange={setEstateAgentIdDisclosure} data-testid="joint-ea-id-disclosure">
                          <div className="flex items-center gap-2"><RadioGroupItem value="yes" id="jea-id-yes" /><label htmlFor="jea-id-yes" className="text-sm text-txt-muted">Yes, I authorise this</label></div>
                          <div className="flex items-center gap-2"><RadioGroupItem value="no" id="jea-id-no" /><label htmlFor="jea-id-no" className="text-sm text-txt-muted">No, I do not authorise this</label></div>
                        </RadioGroup>
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label className="text-txt text-sm">We will not disclose detailed financial information to estate agents without further instructions. Do you agree?</Label>
                      <RadioGroup value={estateAgentDetailLimit} onValueChange={setEstateAgentDetailLimit} data-testid="joint-ea-detail-limit">
                        <div className="flex items-center gap-2"><RadioGroupItem value="yes" id="jea-detail-yes" /><label htmlFor="jea-detail-yes" className="text-sm text-txt-muted">Yes</label></div>
                        <div className="flex items-center gap-2"><RadioGroupItem value="no" id="jea-detail-no" /><label htmlFor="jea-detail-no" className="text-sm text-txt-muted">No, please contact me to discuss any disclosure first</label></div>
                      </RadioGroup>
                    </div>
                    {estateAgentAuthority === "yes" && (
                      <div className="space-y-1.5">
                        <Label className="text-txt text-sm">Please name the agent(s) we may speak to about your matter (if any):</Label>
                        <Input value={estateAgentNames} onChange={e => setEstateAgentNames(e.target.value)}
                          placeholder="e.g. Smith & Jones Estate Agents" className="h-10" data-testid="joint-ea-agent-names" />
                      </div>
                    )}
                  </div>
                )}

                <ClientSignatureField
                  mode={sigMode}
                  onModeChange={setSigMode}
                  typedName={typedSignature}
                  onTypedNameChange={setTypedSignature}
                  canvasRef={sigCanvasRef}
                  fullName={formData.full_name}
                  padTestId="joint-signature-pad"
                  clearTestId="joint-clear-signature"
                  testIdPrefix="joint-"
                />

                {/* Named validation error summary */}
                {errors.length > 0 && (
                  <div className="p-3 rounded-md bg-status-red-dim/30 border border-red-800/40" data-testid="joint-form-error">
                    <p className="text-status-red text-sm font-medium mb-1.5 flex items-center gap-1.5">
                      <AlertCircle className="w-4 h-4" />
                      Please complete the following before submitting:
                    </p>
                    <ul className="space-y-0.5 pl-1">
                      {errors.map((err, i) => (
                        <li key={i} className="text-status-red text-sm flex items-start gap-1.5">
                          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-status-red shrink-0" />
                          {err}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setStep("tob")} className="h-11 border-navy-border text-slate-300">Back</Button>
                  <Button type="submit" disabled={submitting} className="flex-1 h-11 bg-teal hover:bg-teal-hover text-white" data-testid="joint-submit-button">
                    {submitting ? "Submitting..." : "Submit"}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Shell>
  );
}

function StepBadge({ num, label, active, done }) {
  return (
    <div className={`flex items-center gap-2 text-sm font-medium ${active ? "text-teal" : "text-slate-400"}`}>
      <span className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold ${active ? "bg-teal text-white" : done ? "bg-teal-dim/40 text-teal" : "bg-white/5 text-slate-500"}`}>
        {done ? "✓" : num}
      </span>
      {label}
    </div>
  );
}
